import { ADDRESS_NOT_SHARED_VALUE } from 'platform/shared/health-apps';
import get from 'platform/utilities/data/get';
import omit from 'platform/utilities/data/omit';
import set from 'platform/utilities/data/set';
import { applicationStatusPages } from '../chapters/applicationStatus';
import { certifierPages } from '../chapters/certifier';
import { isObject, isSupportingDocument } from '../utils/helpers';

export default [
  // 0 -> 1, flatten certifier relationship, reset stale certifier returnUrl,
  // and migrate address sharing keys
  ({ formData, metadata = {} }) => {
    const flattenCertifierRelationship = data => {
      const { relationshipToVeteran } = data.certifierRelationship ?? {};
      return relationshipToVeteran
        ? set('certifierRelationship', relationshipToVeteran, data)
        : data;
    };

    const migrateSharedAddresses = data => {
      const sourceKeys = ['applicants.applicantAddress', 'sponsorAddress'];

      const stringifyAddress = address => {
        try {
          return address ? JSON.stringify(address) : null;
        } catch {
          return null;
        }
      };

      const sourceValueGetters = {
        sponsorAddress: () => {
          const sponsorAddress = get('sponsorAddress', data);
          return sponsorAddress ? [sponsorAddress] : [];
        },
        'applicants.applicantAddress': excludeIndex =>
          (get('applicants', data, []) || [])
            .filter((_, idx) => idx !== excludeIndex)
            .map(applicant => applicant?.applicantAddress)
            .filter(Boolean),
      };

      const buildValidOptionSet = excludeIndex =>
        new Set(
          sourceKeys
            .flatMap(key => sourceValueGetters[key]?.(excludeIndex) || [])
            .map(stringifyAddress)
            .filter(Boolean),
        );

      const normalizeSharedAddressValue = (value, excludeIndex) => {
        if (value === undefined) return undefined;

        const validOptions = buildValidOptionSet(excludeIndex);
        return validOptions.has(value) ? value : ADDRESS_NOT_SHARED_VALUE;
      };

      const migrateApplicant = (applicant, index) => {
        const { 'view:sharesAddressWith': sharedAddress, ...rest } =
          applicant ?? {};

        if (sharedAddress === undefined) return applicant;

        return set(
          'view:applicantSharedAddress',
          normalizeSharedAddressValue(sharedAddress, index),
          rest,
        );
      };

      let result = omit(['view:sharesAddressWith'], data);

      if (data.applicants?.length) {
        result = set(
          'applicants',
          data.applicants.map(migrateApplicant),
          result,
        );
      }

      const certifierAddress = get('certifierAddress', data);
      if (certifierAddress) {
        const certifierAddressValue = stringifyAddress(certifierAddress);
        const validOptions = buildValidOptionSet();
        result = set(
          'view:certifierSharedAddress',
          validOptions.has(certifierAddressValue)
            ? certifierAddressValue
            : ADDRESS_NOT_SHARED_VALUE,
          result,
        );
      }

      return result;
    };

    const migrateReturnUrl = currentMetadata => {
      const returnUrl =
        currentMetadata.returnUrl ?? currentMetadata.return_url ?? '';

      const hasCertifierPath = Object.values(certifierPages)
        .map(({ path }) => `/${path}`)
        .some(segment => returnUrl.includes(segment));

      return hasCertifierPath
        ? set(
            'returnUrl',
            `/${applicationStatusPages.certifierRoleNew.path}`,
            currentMetadata,
          )
        : currentMetadata;
    };

    return {
      formData: migrateSharedAddresses(flattenCertifierRelationship(formData)),
      metadata: migrateReturnUrl(metadata),
    };
  },

  // 1 -> 2, remap legacy returnUrl paths to IA-approved routes
  ({ formData, metadata = {} }) => {
    const url = metadata.returnUrl || metadata.return_url;
    let newMetadata = metadata;

    if (url === '/who-is-applying') {
      newMetadata = set('returnUrl', '/your-role-in-this-submission', metadata);
    }

    return { formData, metadata: newMetadata };
  },

  // 2 -> 3, map supporting docs data for updated file input structure
  ({ formData, metadata = {} }) => {
    const flattenAttachmentArrays = data => {
      if (Array.isArray(data)) {
        if (data.length > 0 && isSupportingDocument(data[0])) return data[0];
        return data.map(flattenAttachmentArrays);
      }

      if (!isObject(data)) return data;

      return Object.fromEntries(
        Object.entries(data).map(([key, value]) => [
          key,
          flattenAttachmentArrays(value),
        ]),
      );
    };

    return {
      formData: flattenAttachmentArrays(formData),
      metadata,
    };
  },

  // 3 -> 4, normalize legacy submissionType values
  ({ formData, metadata = {} }) => {
    let newData = formData;

    if (formData?.submissionType === 'enrollment') {
      newData = set('submissionType', 'existing', formData);
    }

    return { formData: newData, metadata };
  },

  // 4 -> 5, flatten previously nested datasets
  ({ formData, metadata }) => {
    const keysToFlatten = [
      'applicantGender',
      'applicantRelationshipToSponsor',
      'applicantRelationshipOrigin',
      'applicantDependentStatus',
    ];

    const flattenNestedValue = value =>
      isObject(value) ? Object.values(value)[0] : value;

    const applicants = (formData.applicants || []).map(applicant =>
      keysToFlatten.reduce((data, key) => {
        const value = get(key, data);
        return value === undefined
          ? data
          : set(key, flattenNestedValue(value), data);
      }, applicant),
    );

    return {
      formData: {
        ...formData,
        applicants,
      },
      metadata,
    };
  },
];
